import React, { useEffect, useRef, useState } from 'react';
import { Lock, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { api } from '../services/api';
import { businessToday } from '../utils/time';

/**
 * The change order sheet — how a bid is changed after the customer accepted it.
 *
 * The owner, Oct 6 2026: "can you allow me to make change orders on bids? So if
 * we are going to change a bid, but it's already accepted, click change order
 * so I can change something on the bid and then resend."
 *
 * He taps Change order on an accepted bid and this opens: change a line, take
 * one off, add one, say why — then send it to the customer to approve, or
 * note that they already said yes. Nothing on the bid, the calendar or a bill
 * moves until then.
 *
 * There is no third way to finish. Asked whether a change that only lowers the
 * price may be applied right away, he said (Oct 6 2026): "make them accept,
 * not a new code". So a lower price is sent like any other change, and the
 * customer accepts it with one tap — the sheet says so above the buttons.
 *
 * It is a sheet of its own, not the bid's line editor re-pointed: that editor
 * writes a draft bid in place, and an accepted bid must never be written in
 * place. Every word about money, where a line lands and why something cannot
 * be done comes from the server's one preview (services/changeOrders.planFrom)
 * — this file lays it out and sends back what he typed. So the sheet, the
 * customer's page and the apply can never disagree.
 *
 * Only the owner reaches it (the button is his alone; the routes refuse anyone
 * else). The office reads "bid" and "change order"; the text shown under
 * "What they will get" is the customer's, who reads "quote" and "change".
 */

type Line = {
  id: number; description: string; quantity: number; unit_price: number; total: number; unit: string | null;
  is_optional: number; is_selected: number; sub_cost: number | null; markup_pct: number | null;
  locked: boolean; lock_words: string | null; can: string[];
};
type Change = {
  op: 'add' | 'remove' | 'change'; line_id: number | null; key: string; description: string; quantity: number; unit_price: number;
  total: number; sub_cost?: number | null; markup_pct?: number | null;
};
type Row = { key: string; op: 'add' | 'remove' | 'change'; words: string; lands: string | null; blocked: string | null; where: { kind?: string } | null };
type Money = { once: { was: number; now: number; delta: number }; per_visit: number | null; per_month: number | null };
type Preview = {
  rows: Row[]; money: Money; sentence: string; deposit: { words: string | null; blocked: boolean } | null; mismatch: string | null;
  blockers: string[]; why_not: string | null; can: { send: boolean; noted_yes: boolean };
  // lower_only: it only lowers the price — they accept it with one tap, no code. lower_words says so, with their name.
  lower_only?: boolean; lower_words?: string | null;
  text_preview: string; texts: { needs_yes: string; noted_yes: string };
  open_visits: { job_id: number; date: string; words: string }[]; nothing_booked: boolean; stale?: boolean;
  after_view?: { description: string; price: string; mark: 'add' | 'change' | 'remove' | null; was: string | null }[];
};
type Order = {
  id: number; number: number; status: string; reason: string; changes: Change[]; work_date: string | null; work_with_job_id: number | null;
  words: string; sent_label: string | null; sent_via: string | null; sent_to: string | null; apply_error: string | null;
  approved_label?: string | null; approved_name?: string | null;
};
type OptionGroup = { id: number; name: string | null; lock_words: string; picked: { id: number; name: string | null; price: number }[] };
type Data = { co: Order; lines: Line[]; options: OptionGroup[]; preview: Preview };

const usd = (n: number) => `$${Math.abs(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const firstName = (n?: string | null) => String(n || '').trim().split(/\s+/)[0] || 'them';
const err = (e: any, fallback: string) => e?.response?.data?.message || fallback;
const NOTHING = 'Nothing has changed yet.';
/** The reason the way the server keeps it (one line, trimmed), so "is it saved?" is asked of the same words. */
const tidy = (s: string) => s.replace(/\s+/g, ' ').trim().slice(0, 500);

/** The start, save and back-to-edit routes answer { change_order, lines, options, preview }; the preview route answers it flat. */
function shape(d: any): Data {
  if (d?.preview) return { co: d.change_order, lines: d.lines || [], options: d.options || [], preview: d.preview };
  const { change_order, lines, options, ...preview } = d || {};
  return { co: change_order, lines: lines || [], options: options || [], preview: preview as Preview };
}

/** What he typed, the way the save route takes it: a line on the bid by its id, a brand-new line by its key. */
function raw(c: Change) {
  if (c.line_id != null) {
    return c.op === 'change'
      ? { op: c.op, line_id: c.line_id, description: c.description, quantity: c.quantity, unit_price: c.unit_price, sub_cost: c.sub_cost ?? null, markup_pct: c.markup_pct ?? null }
      : { op: c.op, line_id: c.line_id };
  }
  return { op: 'add', key: c.key, description: c.description, quantity: c.quantity, unit_price: c.unit_price, sub_cost: c.sub_cost ?? null, markup_pct: c.markup_pct ?? null };
}

/** "Oct 2" in Austin's time. The database keeps UTC without saying so. */
function shortDate(v?: string | null) {
  if (!v) return null;
  const s = String(v);
  const d = new Date(s.replace(' ', 'T') + (/Z|[+-]\d\d:?\d\d$/.test(s) ? '' : 'Z'));
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Chicago' });
}

type Form = { description: string; quantity: string; price: string; sub: boolean; cost: string; pct: string };
const blank: Form = { description: '', quantity: '1', price: '', sub: false, cost: '', pct: '5' };
const markedUp = (cost: string, pct: string) => {
  const c = Number(cost), p = Number(pct);
  return Number.isFinite(c) && c > 0 && Number.isFinite(p) && p >= 0 ? (Math.round(c * (1 + p / 100) * 100) / 100).toFixed(2) : '';
};

export default function ChangeOrderEditor({ quote, earlierApplied = false, onClose, onChanged }: {
  quote: any; earlierApplied?: boolean; onClose: () => void; onChanged: () => void;
}) {
  const [data, setData] = useState<Data | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  // Which line is being typed over: a bid line's id, 'new' for a line being added, or an added line's key.
  const [editing, setEditing] = useState<{ lineId?: number; key?: string; isNew?: boolean } | null>(null);
  const [form, setForm] = useState<Form>(blank);
  const [panel, setPanel] = useState<'send' | 'yes' | 'back' | null>(null);
  const [how, setHow] = useState<'text' | 'call' | 'in_person' | ''>('');
  const [note, setNote] = useState('');
  const [tell, setTell] = useState(true);
  const [link, setLink] = useState<string | null>(null);
  const who = firstName(quote?.customer_name);

  // Opening the sheet starts a change order — or gets the one already open (one at a time per bid).
  useEffect(() => {
    let live = true;
    api.post(`/quotes/${quote.id}/change-orders`, {})
      .then(r => { if (!live) return; const d = shape(r.data); setData(d); setReason(d.co.reason || ''); })
      .catch(e => { if (live) setFailed(err(e, 'Could not open the change order.')); });
    return () => { live = false; };
  }, [quote.id]);

  // One save at a time, in the order he made them: a reason still being typed must not land after the line he changed next.
  const chain = useRef<Promise<any>>(Promise.resolve());
  function put(body: any): Promise<Data> {
    const id = data!.co.id;
    const run = chain.current.then(async () => shape((await api.put(`/quotes/change-orders/${id}`, body)).data));
    chain.current = run.catch(() => null);
    return run;
  }
  async function save(body: any, what = 'Could not save that') {
    setBusy('save');
    try { setData(await put(body)); return true; }
    catch (e: any) { toast.error(err(e, what)); return false; }
    finally { setBusy(null); }
  }
  const draft = data?.co.status === 'draft';
  const savedReason = data?.co.reason || '';
  // The reason saves itself a moment after he stops typing (a disabled button cannot be relied on to take the focus
  // away on a phone), and again before anything is sent.
  useEffect(() => {
    if (!draft || tidy(reason) === savedReason) return;
    const t = setTimeout(() => { save({ reason }, 'Could not save the reason'); }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reason, draft, savedReason]);
  async function settle() {
    if (draft && tidy(reason) !== savedReason) await save({ reason }, 'Could not save the reason');
    await chain.current;
  }

  // Closing a change order he never put anything in removes it, so the bid does not say "not sent yet" about nothing.
  // One he DID put something in stays — and the bid is told, so its button reads "Change order 1 — not sent yet" and
  // the history line is there the moment the sheet closes (it used to say plain "Change order" until the bid was
  // reopened, and he could walk away believing nothing was pending).
  async function close() {
    if (data && draft) {
      await chain.current;
      if (!data.co.changes.length && !data.co.sent_label) await api.post(`/quotes/change-orders/${data.co.id}/withdraw`, {}).catch(() => null);
      onChanged();
    }
    onClose();
  }

  if (failed || !data) {
    return (
      <Sheet title="Change order" onClose={onClose}>
        <p className={`text-sm ${failed ? 'text-red-700' : 'text-gray-500'}`}>{failed || 'Opening…'}</p>
      </Sheet>
    );
  }

  const { co, lines, options, preview } = data;
  const changes = co.changes || [];
  const rowOf = (key: string) => preview.rows.find(r => r.key === key) || null;
  const changeOf = (lineId: number) => changes.find(c => c.line_id === lineId) || null;
  const added = changes.filter(c => c.line_id == null);
  const setChanges = (next: Change[]) => save({ changes: next.map(raw) }, 'Could not save that change');

  function startChange(line: Line) {
    const c = changeOf(line.id);
    setForm({ ...blank, description: c?.description ?? line.description, quantity: String(c?.quantity ?? line.quantity), price: String(c?.unit_price ?? line.unit_price) });
    setEditing({ lineId: line.id });
  }
  function startNew(c?: Change) {
    setForm(c ? { description: c.description, quantity: String(c.quantity), price: String(c.unit_price), sub: c.sub_cost != null, cost: c.sub_cost != null ? String(c.sub_cost) : '', pct: c.markup_pct != null ? String(c.markup_pct) : '5' } : blank);
    setEditing(c ? { key: c.key } : { isNew: true });
  }
  async function saveForm() {
    if (!editing) return;
    const quantity = Number(form.quantity) || 1;
    const unit_price = Number(form.price);
    const description = form.description.trim();
    if (!description) { toast.error('Say what the line is.'); return; }
    if (!Number.isFinite(unit_price) || form.price === '') { toast.error('Type the price.'); return; }
    const sub = form.sub && Number(form.cost) > 0 ? { sub_cost: Number(form.cost), markup_pct: Number(form.pct) || 0 } : { sub_cost: null, markup_pct: null };
    let next: Change[];
    if (editing.lineId != null) {
      const line = lines.find(l => l.id === editing.lineId)!;
      const one: Change = { op: 'change', line_id: line.id, key: `l${line.id}`, description, quantity, unit_price, total: quantity * unit_price, sub_cost: line.sub_cost, markup_pct: line.markup_pct };
      next = [...changes.filter(c => c.line_id !== line.id), one];
    } else if (editing.key) {
      next = changes.map(c => (c.line_id == null && c.key === editing.key ? { ...c, description, quantity, unit_price, total: quantity * unit_price, ...sub } : c));
    } else {
      next = [...changes, { op: 'add', line_id: null, key: `n${Date.now().toString(36)}`, description, quantity, unit_price, total: quantity * unit_price, ...sub }];
    }
    if (await setChanges(next)) setEditing(null);
  }

  // ── Finishing ──
  async function send(via: 'sms' | 'email' | 'link') {
    setBusy(`send-${via}`);
    try {
      await settle();
      const r = await api.post(`/quotes/change-orders/${co.id}/send`, { via });
      toast.success(r.data?.message || 'Sent', { autoClose: 8000 });
      onChanged();
      if (via !== 'link') { onClose(); return; }
      // The link is his to send: copied when the phone allows it, and shown so he can copy it by hand when it does not.
      setLink(r.data?.link || null);
      try { if (r.data?.link) await navigator.clipboard.writeText(r.data.link); } catch { /* shown below */ }
      setPanel(null);
      setData(shape((await api.get(`/quotes/change-orders/${co.id}/preview`)).data));
    } catch (e: any) { toast.error(err(e, 'That did not send — nothing was changed.')); }
    finally { setBusy(null); }
  }
  async function apply(path: 'noted-yes' | 'retry', body: any) {
    setBusy(path);
    try {
      await settle();
      const r = await api.post(`/quotes/change-orders/${co.id}/${path}`, body);
      toast.success(r.data?.message || 'Change order applied.', { autoClose: 12000 });
      onChanged(); onClose();
    } catch (e: any) {
      toast.error(err(e, 'That did not go through — nothing was changed.'), { autoClose: 10000 });
      // Whatever stopped it (a bill made a moment ago, a line changed on the visit) is on the sheet now.
      api.get(`/quotes/change-orders/${co.id}/preview`).then(r => setData(shape(r.data))).catch(() => null);
      onChanged();
    } finally { setBusy(null); }
  }
  async function backToEdit() {
    setBusy('edit');
    try { const d = shape((await api.post(`/quotes/change-orders/${co.id}/back-to-edit`, {})).data); setData(d); setReason(d.co.reason || ''); setPanel(null); setLink(null); onChanged(); }
    catch (e: any) { toast.error(err(e, 'Could not take it back to edit')); }
    finally { setBusy(null); }
  }
  async function takeBack() {
    setBusy('back');
    try { const r = await api.post(`/quotes/change-orders/${co.id}/withdraw`, co.status === 'approved' ? { tell } : {}); toast.success(r.data?.message || 'Taken back', { autoClose: 10000 }); onChanged(); onClose(); }
    catch (e: any) { toast.error(err(e, 'Could not take it back')); }
    finally { setBusy(null); }
  }

  // ── What the top of the sheet says ──
  const m = preview.money;
  const when = shortDate(quote?.agreement_signed_at || quote?.accepted_at);
  const mixed = !!(m.per_visit || m.per_month);
  const Who = quote?.customer_name ? who : 'They';
  // One figure on a plain bid; on a bid with mowing or a plan the one-time work and the repeating price are said
  // apart, never added up (a $55 mow plus a $350 cleanup is not "$405").
  const accepted = mixed
    ? `${Who} accepted this${when ? ` on ${when}` : ''}: ${[m.once.was > 0 ? `one-time work ${usd(m.once.was)}` : null, m.per_visit ? `mowing ${usd(m.per_visit)} a mow` : null, m.per_month ? `${usd(m.per_month)} a month` : null].filter(Boolean).join(', ')}.`
    : earlierApplied
      ? `${Who} accepted this${when ? ` on ${when}` : ''}; with the change orders since, it stands at ${usd(m.once.was)}.`
      : `${Who} accepted this at ${usd(quote?.agreement_total != null ? Number(quote.agreement_total) : m.once.was)}${when ? ` on ${when}` : ''}.`;

  const adds = preview.rows.filter(r => r.op === 'add');
  // Added work needs a day only when this bid already has work on the calendar and no project to put it on.
  const needsDay = adds.length > 0 && !preview.nothing_booked && !adds.some(r => r.where?.kind === 'project' || /cannot change a bill|^This project/.test(r.blocked || ''));
  const askWhen = draft && needsDay;
  const whyNot = preview.why_not;
  const whyTone = !whyNot ? '' : whyNot === NOTHING ? 'text-gray-500' : /^Say why/.test(whyNot) ? 'text-amber-800' : 'text-red-700';
  const open = busy === null;

  const RowNote = ({ r }: { r: Row | null }) => (!r ? null : r.blocked
    ? <p className="text-xs text-red-700 mt-0.5">{r.blocked}</p>
    : r.lands ? <p className="text-xs text-gray-500 mt-0.5">→ {r.lands}</p> : null);

  const lineForm = (
    <div className="bg-brand-50 rounded-lg p-3 space-y-2">
      <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="input text-sm w-full" placeholder="What it is — Mulch, 3 yards" aria-label="Description" />
      <div className="flex items-center gap-2">
        <input type="number" inputMode="decimal" min="0" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} className="input text-sm w-20" placeholder="Qty" aria-label="Quantity" />
        <span className="text-xs text-gray-500">×</span>
        <div className="relative flex-1">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
          <input type="number" inputMode="decimal" min="0" step="0.01" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} className="input text-sm pl-6 w-full" placeholder="Price each" aria-label="Price each" />
        </div>
        <span className="text-xs text-gray-600 whitespace-nowrap">= {usd((Number(form.quantity) || 1) * (Number(form.price) || 0))}</span>
      </div>
      {editing?.lineId == null && (
        <label className="flex items-center gap-2 text-xs text-gray-600">
          <input type="checkbox" checked={form.sub} onChange={e => setForm(f => ({ ...f, sub: e.target.checked }))} className="rounded text-brand-600" />
          Subcontracted — price it from the sub's cost + a markup
        </label>
      )}
      {editing?.lineId == null && form.sub && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
          <span>Sub's cost $</span>
          <input type="number" inputMode="decimal" min="0" step="0.01" value={form.cost} aria-label="Sub's cost" className="input text-sm w-24"
            onChange={e => setForm(f => ({ ...f, cost: e.target.value, price: markedUp(e.target.value, f.pct) || f.price }))} />
          <span>+</span>
          <input type="number" inputMode="decimal" min="0" step="0.5" value={form.pct} aria-label="Markup percent" className="input text-sm w-16"
            onChange={e => setForm(f => ({ ...f, pct: e.target.value, price: markedUp(f.cost, e.target.value) || f.price }))} />
          <span>%</span>
        </div>
      )}
      <div className="flex gap-2">
        <button type="button" onClick={saveForm} disabled={!open} className="btn-primary text-xs py-1.5 px-3 disabled:opacity-50">{busy === 'save' ? 'Saving…' : editing?.isNew ? 'Add the line' : 'Save'}</button>
        <button type="button" onClick={() => setEditing(null)} className="btn-secondary text-xs py-1.5 px-3">Cancel</button>
      </div>
    </div>
  );

  const sendChoices = (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
      <p className="text-xs text-gray-600">One message with the same link as the bid. Nothing changes until they {preview.lower_only ? 'accept — one tap, no code' : 'approve'}.</p>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => send('sms')} disabled={!open} className="btn-primary text-sm disabled:opacity-50">{busy === 'send-sms' ? 'Sending…' : 'Text it'}</button>
        <button type="button" onClick={() => send('email')} disabled={!open} className="btn-secondary text-sm disabled:opacity-50">{busy === 'send-email' ? 'Sending…' : 'Email it'}</button>
        <button type="button" onClick={() => send('link')} disabled={!open} className="btn-secondary text-sm disabled:opacity-50">{busy === 'send-link' ? 'Getting it…' : 'Copy the link'}</button>
        <button type="button" onClick={() => setPanel(null)} className="text-xs text-gray-500 underline px-1">Cancel</button>
      </div>
    </div>
  );
  const yesPanel = (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
      <p className="text-sm font-semibold text-gray-800">How did they say yes?</p>
      <div className="flex flex-wrap gap-2">
        {([['text', 'Text'], ['call', 'Call'], ['in_person', 'In person']] as const).map(([k, label]) => (
          <button key={k} type="button" onClick={() => setHow(k)} aria-pressed={how === k}
            className={`px-3 py-2 rounded-lg border text-sm font-medium ${how === k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-800 border-gray-300'}`}>{label}</button>
        ))}
      </div>
      <input value={note} onChange={e => setNote(e.target.value)} className="input text-sm w-full" placeholder="A note for the record (optional)" aria-label="Note" />
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={tell} onChange={e => setTell(e.target.checked)} className="rounded text-brand-600" /> Text them a copy
      </label>
      {tell && co.status === 'sent' && <p className="text-xs text-gray-500">They already have the link from when it was sent, so no second message goes out.</p>}
      <div className="flex gap-2">
        <button type="button" onClick={() => apply('noted-yes', { how, note: note.trim() || undefined, tell })} disabled={!open || !how} className="btn-primary text-sm disabled:opacity-50">{busy === 'noted-yes' ? 'Applying…' : 'Apply it now'}</button>
        <button type="button" onClick={() => setPanel(null)} className="btn-secondary text-sm">Cancel</button>
      </div>
      {!how && <p className="text-xs text-gray-500">Pick how they said yes.</p>}
    </div>
  );
  const whenPicker = (
    <div>
      <p className="text-sm font-semibold text-gray-800 mb-1.5">When do we do the added work?</p>
      <div className="space-y-1.5">
        {preview.open_visits.map(v => (
          <label key={v.job_id} className="flex items-center gap-2 text-sm text-gray-800 rounded-lg border border-gray-200 px-3 py-2">
            <input type="radio" name="co-when" checked={Number(co.work_with_job_id) === Number(v.job_id)} disabled={!open}
              onChange={() => save({ work_with_job_id: v.job_id, work_date: null }, 'Could not save the day')} />
            {v.words}
          </label>
        ))}
        <label className="flex flex-wrap items-center gap-2 text-sm text-gray-800 rounded-lg border border-gray-200 px-3 py-2">
          {preview.open_visits.length > 0 && (
            <input type="radio" name="co-when" checked={!co.work_with_job_id && !!co.work_date} disabled={!open}
              onChange={() => { if (co.work_with_job_id) save({ work_with_job_id: null }, 'Could not save the day'); }} />
          )}
          <span>{preview.open_visits.length > 0 ? 'Another day' : 'On'}</span>
          <input type="date" min={businessToday()} value={co.work_with_job_id ? '' : (co.work_date || '')} disabled={!open} aria-label="The day for the added work" className="input text-sm py-1.5 w-auto"
            onChange={e => { if (e.target.value) save({ work_with_job_id: null, work_date: e.target.value }, 'Could not save the day'); }} />
        </label>
        {!preview.open_visits.length && <p className="text-xs text-gray-500">No visit from this bid is still open, so the added work is a new visit on the day you pick.</p>}
      </div>
    </div>
  );
  const backPanel = (
    <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-2">
      <p className="text-sm text-red-900">Take change order {co.number} back? {co.status === 'approved' ? 'They approved it, but it was never applied — nothing on the bid, the calendar or a bill changed. ' : ''}The bid stays as they accepted it{co.status === 'sent' ? ', and their link stops offering the change' : ''}.</p>
      {co.status === 'approved' && (
        <label className="flex items-start gap-2 text-sm text-red-900">
          <input type="checkbox" checked={tell} onChange={e => setTell(e.target.checked)} className="rounded text-brand-600 mt-0.5" />
          <span>Text them that the change was not made and that you will be in touch</span>
        </label>
      )}
      <div className="flex gap-2">
        <button type="button" onClick={takeBack} disabled={!open} className="text-sm font-semibold text-white bg-red-600 rounded-lg px-3 py-2 disabled:opacity-50">{busy === 'back' ? 'Taking it back…' : 'Yes, take it back'}</button>
        <button type="button" onClick={() => setPanel(null)} className="btn-secondary text-sm">Keep it</button>
      </div>
    </div>
  );
  const summary = (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-1.5">
      {/* The whole bid as it will read — what they are shown on their page too (owner, Oct 6 2026: "change order change the whole bid"). */}
      {(preview.after_view || []).length > 0 && preview.rows.length > 0 && (
        <div className="rounded-lg border border-gray-200 p-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">The whole bid after this change — they see this</p>
          <ul className="space-y-1">
            {(preview.after_view || []).map((l, k) => (
              <li key={k} className="flex items-baseline justify-between gap-3 text-sm">
                <span className={`min-w-0 ${l.mark === 'remove' ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                  {l.description}{l.mark && <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-brand-700 no-underline">{l.mark === 'add' ? 'new' : l.mark === 'change' ? 'changed' : 'removed'}</span>}
                </span>
                <span className={`flex-shrink-0 tabular-nums ${l.mark === 'remove' ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{l.price}{l.was ? <span className="text-xs text-gray-500"> (was {l.was})</span> : null}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-sm font-bold text-gray-900">{preview.sentence}</p>
      {preview.deposit?.words && <p className={`text-xs ${preview.deposit.blocked ? 'text-red-700 font-semibold' : 'text-gray-700'}`}>{preview.deposit.words}</p>}
    </div>
  );

  return (
    <Sheet title={`Change order ${co.number} — ${quote?.title || 'bid'}`} onClose={close}>
      {/* ── Waiting on the customer ── */}
      {co.status === 'sent' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">{co.words}</p>
          {(co.sent_to || co.sent_via) && <p className="text-xs text-gray-500">Sent{co.sent_label ? ` ${co.sent_label}` : ''}{co.sent_via === 'sms' ? ' by text' : co.sent_via === 'email' ? ' by email' : co.sent_via === 'link' ? ' — you copied the link' : ''}{co.sent_to ? ` to ${co.sent_to}` : ''}. Nothing changes until they {preview.lower_only ? 'accept' : 'approve'}.</p>}
          {preview.lower_only && <p className="text-xs text-gray-600">This lowers the price, so they accept it with one tap — no code.</p>}
          {link && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Their link</label>
              <input readOnly value={link} onClick={e => (e.target as HTMLInputElement).select()} className="input text-sm w-full bg-gray-50" aria-label="Their link" />
            </div>
          )}
          {co.reason && <p className="text-sm text-gray-600 italic">“{co.reason}”</p>}
          <ul className="space-y-2">
            {preview.rows.map(r => <li key={r.key}><p className="text-sm text-gray-900">{r.words}</p><RowNote r={r} /></li>)}
          </ul>
          {summary}
          {preview.stale && <p className="text-sm text-red-700">{whyNot}</p>}
          {!preview.stale && whyNot && whyNot !== NOTHING && <p className="text-sm text-red-700">{whyNot}</p>}
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setPanel(panel === 'send' ? null : 'send')} disabled={!preview.can.send} className="btn-secondary text-sm disabled:opacity-50">Send again</button>
            <button type="button" onClick={backToEdit} disabled={!open} className="btn-secondary text-sm disabled:opacity-50">{busy === 'edit' ? 'Taking it back…' : 'Take it back to edit'}</button>
            <button type="button" onClick={() => setPanel(panel === 'yes' ? null : 'yes')} disabled={!preview.can.noted_yes} className="btn-primary text-sm disabled:opacity-50">They said yes — apply it now</button>
            <button type="button" onClick={() => setPanel(panel === 'back' ? null : 'back')} className="text-sm text-red-600 border border-red-200 rounded-lg px-3 py-2">Take it back</button>
          </div>
          {panel === 'send' && sendChoices}
          {panel === 'yes' && yesPanel}
          {panel === 'back' && backPanel}
        </div>
      )}

      {/* ── They approved, but it could not go on the calendar ── */}
      {co.status === 'approved' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">{co.approved_name || Who} approved this{co.approved_label ? ` ${co.approved_label}` : ''}. It is not on the calendar yet — nothing was changed.</p>
          <ul className="space-y-2">
            {preview.rows.map(r => <li key={r.key}><p className="text-sm text-gray-900">{r.words}</p><RowNote r={r} /></li>)}
          </ul>
          {summary}
          {/* What is in the way, said ONCE: under its row when it is about a row, in the box above when it is the deposit, else here. */}
          {whyNot && !preview.rows.some(r => r.blocked === whyNot) && whyNot !== preview.deposit?.words && <p className="text-sm text-red-700">{whyNot}</p>}
          {!whyNot && <p className="text-sm text-emerald-800">Nothing is in the way now — tap Try again.</p>}
          {needsDay && !preview.stale && whenPicker}
          <p className="text-xs text-gray-500">Their yes is on record: what changes and what it costs stay as they approved.{needsDay ? ' The day for the added work is yours to move.' : ''} If it cannot be done at all, take it back and start a new one.</p>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => apply('retry', {})} disabled={!open} className="btn-primary text-sm disabled:opacity-50">{busy === 'retry' ? 'Trying…' : 'Try again'}</button>
            <button type="button" onClick={() => setPanel(panel === 'back' ? null : 'back')} className="text-sm text-red-600 border border-red-200 rounded-lg px-3 py-2">Take it back</button>
          </div>
          {panel === 'back' && backPanel}
        </div>
      )}

      {/* ── Finished: nothing to do here, the bid's history has it ── */}
      {!['draft', 'sent', 'approved'].includes(co.status) && <p className="text-sm text-gray-700">{co.words}</p>}

      {/* ── Being written ── */}
      {draft && (
        <div className="space-y-5">
          <p className="text-sm text-gray-700">{accepted} Change what you need, then send it. Nothing on the calendar or the bill moves until it is approved.</p>
          {preview.mismatch && <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2">{preview.mismatch}</p>}
          {preview.stale && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-2.5 py-2">{whyNot}</p>}

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">The bid's lines</p>
            <div className="space-y-1.5">
              {lines.map(line => {
                const c = changeOf(line.id);
                const r = c ? rowOf(c.key) : null;
                const off = !line.is_selected && !c;
                if (editing?.lineId === line.id) return <div key={line.id}>{lineForm}</div>;
                return (
                  <div key={line.id} className={`rounded-lg px-3 py-2 ${c ? 'bg-brand-50 border border-brand-200' : line.locked ? 'bg-gray-50' : 'bg-white border border-gray-200'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className={`min-w-0 flex-1 ${off || c?.op === 'remove' ? 'opacity-60' : ''}`}>
                        <p className={`text-sm text-gray-900 ${c?.op === 'remove' ? 'line-through' : ''}`}>{line.description}</p>
                        {line.quantity !== 1 && <p className="text-xs text-gray-500">{line.quantity} × {usd(line.unit_price)}</p>}
                      </div>
                      <p className={`text-sm font-semibold whitespace-nowrap ${off || c?.op === 'remove' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                        {usd(line.total)}{line.unit === 'visit' ? <span className="text-xs font-normal text-gray-500">/visit</span> : line.unit === 'month' ? <span className="text-xs font-normal text-gray-500">/mo</span> : line.unit === 'year' ? <span className="text-xs font-normal text-gray-500">/yr</span> : null}
                      </p>
                    </div>
                    {c && r && <p className="text-sm font-medium text-brand-900 mt-1">{r.words}</p>}
                    {c && <RowNote r={r} />}
                    {line.locked && <p className="text-xs text-gray-500 mt-1 flex items-start gap-1"><Lock className="h-3 w-3 mt-0.5 flex-shrink-0" aria-hidden /> <span>{line.lock_words}</span></p>}
                    {!line.locked && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {c && <button type="button" onClick={() => setChanges(changes.filter(x => x.line_id !== line.id))} disabled={!open} className="text-xs font-semibold text-gray-700 border border-gray-300 rounded-lg px-3 py-1.5 bg-white disabled:opacity-50">Undo</button>}
                        {(!c || c.op === 'change') && line.can.includes('change') && <button type="button" onClick={() => startChange(line)} disabled={!open} className="text-xs font-semibold text-brand-800 border border-brand-300 rounded-lg px-3 py-1.5 bg-white disabled:opacity-50">Change</button>}
                        {!c && line.can.includes('remove') && <button type="button" onClick={() => setChanges([...changes, { op: 'remove', line_id: line.id, key: `l${line.id}`, description: line.description, quantity: line.quantity, unit_price: line.unit_price, total: line.total }])} disabled={!open} className="text-xs font-semibold text-red-700 border border-red-200 rounded-lg px-3 py-1.5 bg-white disabled:opacity-50">Remove</button>}
                        {!c && line.can.includes('add') && <button type="button" onClick={() => setChanges([...changes, { op: 'add', line_id: line.id, key: `l${line.id}`, description: line.description, quantity: line.quantity, unit_price: line.unit_price, total: line.total }])} disabled={!open} className="text-xs font-semibold text-emerald-800 border border-emerald-300 rounded-lg px-3 py-1.5 bg-white disabled:opacity-50">Add this option</button>}
                      </div>
                    )}
                  </div>
                );
              })}
              {options.map(g => (
                <div key={`g${g.id}`} className="rounded-lg px-3 py-2 bg-gray-50">
                  <p className="text-sm text-gray-900">{g.name || 'Option'}{g.picked[0] ? `: ${g.picked[0].name || 'picked'}` : ''}{g.picked[0] && g.picked[0].price > 0 ? ` · ${usd(g.picked[0].price)}` : ''}</p>
                  <p className="text-xs text-gray-500 mt-1 flex items-start gap-1"><Lock className="h-3 w-3 mt-0.5 flex-shrink-0" aria-hidden /> <span>{g.lock_words}</span></p>
                </div>
              ))}
              {added.map(c => {
                const r = rowOf(c.key);
                if (editing?.key === c.key) return <div key={c.key}>{lineForm}</div>;
                return (
                  <div key={c.key} className="rounded-lg px-3 py-2 bg-emerald-50 border border-emerald-200">
                    <p className="text-sm font-medium text-emerald-900">{r?.words || `Added · ${c.description}`}</p>
                    {/* The row's own words say how many ("3 × $95.00") — the customer reads the same line. */}
                    {c.sub_cost != null && <p className="text-[11px] text-gray-500">Sub · cost {usd(c.sub_cost)} + {Number(c.markup_pct) || 0}%</p>}
                    <RowNote r={r} />
                    <div className="flex flex-wrap gap-2 mt-2">
                      <button type="button" onClick={() => setChanges(changes.filter(x => !(x.line_id == null && x.key === c.key)))} disabled={!open} className="text-xs font-semibold text-gray-700 border border-gray-300 rounded-lg px-3 py-1.5 bg-white disabled:opacity-50">Undo</button>
                      <button type="button" onClick={() => startNew(c)} disabled={!open} className="text-xs font-semibold text-brand-800 border border-brand-300 rounded-lg px-3 py-1.5 bg-white disabled:opacity-50">Change</button>
                    </div>
                  </div>
                );
              })}
              {editing?.isNew ? lineForm : (
                <button type="button" onClick={() => startNew()} disabled={!open || !!preview.stale} className="text-sm font-semibold text-brand-700 py-2 disabled:opacity-50">+ Add a line</button>
              )}
            </div>
          </div>

          {askWhen && whenPicker}

          <div>
            <label htmlFor="co-why" className="block text-sm font-semibold text-gray-800 mb-1">Why (they see this)</label>
            <textarea id="co-why" value={reason} onChange={e => setReason(e.target.value)} onBlur={() => { if (tidy(reason) !== savedReason) save({ reason }, 'Could not save the reason'); }}
              rows={2} maxLength={500} className="input text-sm w-full" placeholder="You asked us to add the back bed" />
          </div>

          {changes.length > 0 && summary}

          {changes.length > 0 && !whyNot && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">What they will get</p>
              {/* The exact words, for the way he is about to finish — or that nothing goes when he unticked the copy. */}
              <p className="text-sm text-gray-800 bg-white border border-gray-200 rounded-lg px-3 py-2 whitespace-pre-wrap break-words">
                {panel === 'yes' && !tell ? 'Nothing — you are not texting them a copy. Their link shows the change.'
                  : panel === 'yes' ? preview.texts.noted_yes : preview.text_preview}
              </p>
            </div>
          )}

          <div className="space-y-2 border-t border-gray-200 pt-4">
            {/* A change that only lowers the price is not applied by the office any more: it is sent, and they accept it with one tap. */}
            {changes.length > 0 && preview.lower_words && <p className="text-sm text-gray-800">{preview.lower_words}</p>}
            <button type="button" onClick={() => setPanel(panel === 'send' ? null : 'send')} disabled={!preview.can.send} className="w-full py-3 rounded-xl bg-brand-600 text-black text-sm font-bold disabled:opacity-40">Send it to {who} to approve</button>
            {panel === 'send' && preview.can.send && sendChoices}
            <button type="button" onClick={() => setPanel(panel === 'yes' ? null : 'yes')} disabled={!preview.can.noted_yes} className="w-full py-3 rounded-xl border border-gray-300 bg-white text-gray-900 text-sm font-semibold disabled:opacity-40">They already said yes — apply it now</button>
            {panel === 'yes' && preview.can.noted_yes && yesPanel}
            {whyNot && <p className={`text-sm ${whyTone}`}>{whyNot}</p>}
            {changes.length > 0 && (
              <button type="button" onClick={() => setPanel(panel === 'back' ? null : 'back')} className="text-xs text-gray-500 underline pt-1">Take it back — throw this change order away</button>
            )}
            {panel === 'back' && backPanel}
          </div>
        </div>
      )}
    </Sheet>
  );
}

/** Full screen on a phone (he mostly uses his phone), a centred card on a desk. */
function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-stretch sm:items-center justify-center sm:p-4 bg-black/50" role="dialog" aria-modal="true" aria-label={title}>
      <div className="bg-white w-full sm:max-w-xl sm:rounded-xl shadow-xl flex flex-col h-full sm:h-auto sm:max-h-[92vh] overflow-hidden">
        <div className="flex items-start justify-between gap-3 px-4 sm:px-5 py-3.5 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-base font-black uppercase tracking-tight text-gray-900 min-w-0 break-words">{title}</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="flex-shrink-0 p-1 -m-1"><X className="h-5 w-5 text-gray-500" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4">{children}</div>
      </div>
    </div>
  );
}
